import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Typography } from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import { getIn } from 'formik';
import type { FormikProps } from 'formik';
import { QRCodeSVG } from 'qrcode.react';
import CustomButton from 'src/core/ui/CustomButton';
import { CustomField } from 'src/core/forms/CustomField';
import { ReviewTextField } from 'src/core/forms/FieldReview';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { useMessageContext } from 'src/shared/support/MessageContext';

// Mount beside the card, not inside its grouped menu.
export const QZServerDialog = ({ open, onClose, ...props }: QZServerDialogProps) => {
  const titleId = useRef(`qz-server-${Math.random().toString(36).slice(2)}`);
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby={titleId.current} onKeyDown={(event) => event.stopPropagation()} onKeyUp={(event) => event.stopPropagation()} onSubmit={(event) => { event.preventDefault(); event.stopPropagation(); }}>
    {open && <QZServerGuide {...props} titleId={titleId.current} />}
    <DialogActions><Box sx={{ width: 'fit-content' }}><CustomButton title="Close" variant="text" onClick={onClose} /></Box></DialogActions>
  </Dialog>;
};

export const useQZServerConfiguration = () => {
  const [open, setOpen] = useState(false);
  return { openDialog: () => setOpen(true), dialogProps: { open, onClose: () => setOpen(false) } };
};

export const QZServerButton = ({ onClick, variant = 'contained' }: { onClick: () => void; variant?: 'contained' | 'outlined' | 'text' }) => <CustomButton title="Configure QZ Print Server" variant={variant} icon={<DnsIcon />} onClick={onClick} />;

// One visible field; retain the existing two settings keys for Printer Manager compatibility.
export const QZServerFields = ({ formik, disabled = false, resetKey = 0 }: { formik: FormikProps<any>; disabled?: boolean; resetKey?: number }) => {
  const value = readSettings(formik);
  const [address, setAddress] = useState(formatAddress(value));
  useEffect(() => { setAddress(formatAddress(value)); }, [value.qzHost, value.qzPort, resetKey]);
  const errors = validateQZServer(parseQZAddress(address));
  const error = errors.qzHost || errors.qzPort;
  // One address edits two settings; preserve immediate validation, autocomplete and blur normalization.
  return <ReviewTextField fullWidth size="small" label="QZ server" placeholder="192.168.1.20 or 192.168.1.20:8181" value={address} disabled={disabled} autoComplete="off" error={Boolean(error)} helperText={error || 'Optional :port. Leave it out for QZ defaults.'} onChange={(event) => {
    const next = event.target.value;
    setAddress(next);
    const parsed = parseQZAddress(next);
    void formik.setValues((previous: any) => ({ ...previous, DispatchPrinting: { ...previous.DispatchPrinting, ...parsed } }));
  }} onBlur={() => { setAddress(formatAddress(readSettings(formik))); void formik.setFieldTouched('DispatchPrinting.qzHost', true); }} />;
};

export const parseQZAddress = (address: string): QZServerSettings => {
  const [host, ...port] = address.trim().split(':');
  return { qzHost: host.trim(), qzPort: port.join(':').trim() };
};

const formatAddress = ({ qzHost, qzPort }: QZServerSettings) => `${qzHost}${qzPort ? `:${qzPort}` : ''}`;

const readSettings = (formik: FormikProps<any>): QZServerSettings => ({ qzHost: String(getIn(formik.values, 'DispatchPrinting.qzHost') ?? ''), qzPort: String(getIn(formik.values, 'DispatchPrinting.qzPort') ?? '') });

export const validateQZServer = ({ qzHost, qzPort }: QZServerSettings): Partial<Record<keyof QZServerSettings, string>> => {
  const host = qzHost.trim(), port = qzPort.trim();
  const errors: Partial<Record<keyof QZServerSettings, string>> = {};
  const ipv4 = /^\d+\.\d+\.\d+\.\d+$/.test(host);
  const validHost = host.length <= 253 && (ipv4 ? host.split('.').every((part) => /^(0|[1-9]\d{0,2})$/.test(part) && Number(part) <= 255) : host.split('.').every((part) => /^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/i.test(part)));
  if (host && !validHost) errors.qzHost = 'Use an IPv4 address or hostname, without http:// or a path.';
  if (port && (!/^\d+$/.test(port) || Number(port) < 1 || Number(port) > 65535)) errors.qzPort = 'Port must be 1–65535.';
  if (port && !host) errors.qzHost = 'Enter a server address.';
  return errors;
};

const Step = ({ title, children }: { title: string; children: ReactNode }) => <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1.5, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
  <Typography fontWeight={600}>{title}</Typography>
  {children}
</Box>;

// Windows desktop QZ Tray 2.2+. Run once in an elevated PowerShell window.
export const QZ_SERVER_SETUP_SCRIPT = String.raw`& {
    $ErrorActionPreference = 'Stop'
    $taskName = $null
    try {
        $identity = [Security.Principal.WindowsIdentity]::GetCurrent()
        $principal = New-Object Security.Principal.WindowsPrincipal($identity)
        if (-not $principal.IsInRole([Security.Principal.WindowsBuiltInRole]::Administrator)) {
            throw 'Open PowerShell as administrator, then paste this script again.'
        }
        $qzFolder = @($env:ProgramFiles, ([Environment]::GetEnvironmentVariable('ProgramFiles(x86)'))) | Where-Object { $_ } | ForEach-Object { Join-Path $_ 'QZ Tray' } | Where-Object { Test-Path (Join-Path $_ 'qz-tray-console.exe') } | Select-Object -First 1
        if (-not $qzFolder) { throw 'QZ Tray 2.2+ was not found in Program Files.' }
        $qzConsole = Join-Path $qzFolder 'qz-tray-console.exe'
        $qzExe = Join-Path $qzFolder 'qz-tray.exe'
        if (-not (Test-Path $qzExe)) { throw 'qz-tray.exe was not found.' }
        $services = @(Get-CimInstance Win32_Service | Where-Object { $_.State -eq 'Running' -and $_.PathName -match 'qz-tray|QZ Tray' })
        if ($services.Count) { throw 'This helper is for desktop QZ Tray. Ask your administrator to configure the running QZ Windows service.' }

        $addresses = @(Get-NetIPConfiguration | Where-Object { $_.NetAdapter.Status -eq 'Up' -and $_.NetAdapter.HardwareInterface -and $_.IPv4DefaultGateway } | ForEach-Object {
            $adapter = $_
            $_.IPv4Address | Where-Object { $_.IPAddress -notmatch '^(127\.|169\.254\.)' } | ForEach-Object {
                [pscustomobject]@{ IP = $_.IPAddress; Index = $adapter.InterfaceIndex; Name = $adapter.InterfaceAlias }
            }
        })
        if (-not $addresses.Count) { throw 'No active Ethernet/Wi-Fi IPv4 address was found. Connect to the phone/tablet LAN first.' }
        $selected = $addresses[0]
        if ($addresses.Count -gt 1) {
            for ($i = 0; $i -lt $addresses.Count; $i++) { Write-Host ('{0}. {1} - {2}' -f ($i + 1), $addresses[$i].Name, $addresses[$i].IP) }
            $choice = Read-Host 'Choose the network used by your phone/tablet'
            $number = 0
            if (-not [int]::TryParse($choice, [ref]$number) -or $number -lt 1 -or $number -gt $addresses.Count) { throw 'Invalid network selection.' }
            $selected = $addresses[$number - 1]
        }
        $serverIP = $selected.IP
        Write-Host "Using $($selected.Name): $serverIP"
        $profiles = @(Get-NetConnectionProfile -InterfaceIndex $selected.Index)
        if (-not $profiles.Count) { throw 'No Windows network profile was found for this adapter.' }
        if ($profiles.NetworkCategory -contains 'Public') {
            if ((Read-Host 'Make this trusted LAN Private to allow printing? (y/N)') -ne 'y') { throw 'Setup stopped; network profile was not changed.' }
            Set-NetConnectionProfile -InterfaceIndex $selected.Index -NetworkCategory Private
        }

        # Stop only QZ Tray processes belonging to this desktop session and installation.
        $session = (Get-Process -Id $PID).SessionId
        $desktop = Get-CimInstance Win32_Process -Filter "Name = 'explorer.exe'" | Where-Object { $_.SessionId -eq $session } | Select-Object -First 1
        if (-not $desktop) { throw 'Run this from the signed-in Windows desktop, not a service or remote shell.' }
        $desktopOwner = Invoke-CimMethod -InputObject $desktop -MethodName GetOwnerSid
        if ($desktopOwner.ReturnValue -ne 0 -or -not $desktopOwner.Sid) { throw 'Could not identify the signed-in desktop user.' }
        $qzProcesses = @(Get-CimInstance Win32_Process | Where-Object { $_.ExecutablePath -in @($qzExe, $qzConsole) })
        if (@($qzProcesses | Where-Object { $_.SessionId -ne $session }).Count) { throw 'QZ is running in another Windows session. Close that instance first.' }
        foreach ($process in $qzProcesses) {
            Stop-Process -Id $process.ProcessId -Force
            Wait-Process -Id $process.ProcessId -Timeout 10 -ErrorAction SilentlyContinue
        }
        Write-Host 'Configuring certificate...'
        & $qzConsole certgen --hosts "localhost;localhost.qz.io;127.0.0.1;$serverIP"
        if ($LASTEXITCODE -ne 0) { throw "QZ certificate generation failed (exit $LASTEXITCODE)." }

        $ruleName = 'ERP-QZ-Print-Server'
        Get-NetFirewallRule -Name $ruleName -ErrorAction SilentlyContinue | Remove-NetFirewallRule
        New-NetFirewallRule -Name $ruleName -DisplayName 'QZ Print Server (local network)' -Direction Inbound -Protocol TCP -LocalPort 8181,8182,8282,8283,8383,8384,8484,8485 -Action Allow -Profile Private,Domain -RemoteAddress LocalSubnet -InterfaceAlias $selected.Name | Out-Null

        # Restart as the existing desktop user with normal permissions; remove the temporary task below.
        Write-Host 'Restarting QZ Tray...'
        $taskName = 'ERP-QZ-Start-' + [guid]::NewGuid().ToString('N')
        $action = New-ScheduledTaskAction -Execute $qzExe -WorkingDirectory $qzFolder
        $taskUser = New-ScheduledTaskPrincipal -UserId $desktopOwner.Sid -LogonType Interactive -RunLevel Limited
        Register-ScheduledTask -TaskName $taskName -Action $action -Principal $taskUser | Out-Null
        Start-ScheduledTask -TaskName $taskName
        $securePorts = @(8181,8282,8383,8484)
        $httpPorts = @(8182,8283,8384,8485)
        $printPort = $null
        $certificateUrl = $null
        $lastFailure = 'QZ did not open its default ports.'
        $deadline = (Get-Date).AddSeconds(45)
        while ((Get-Date) -lt $deadline -and -not $printPort) {
            $runningIds = @(Get-CimInstance Win32_Process | Where-Object { $_.ExecutablePath -eq $qzExe -and $_.SessionId -eq $session } | Select-Object -ExpandProperty ProcessId)
            $listeners = @(Get-NetTCPConnection -State Listen -ErrorAction SilentlyContinue | Where-Object { $_.OwningProcess -in $runningIds } | Select-Object -ExpandProperty LocalPort)
            for ($i = 0; $i -lt $securePorts.Count; $i++) {
                if ($securePorts[$i] -notin $listeners -or $httpPorts[$i] -notin $listeners) { continue }
                $socket = New-Object System.Net.WebSockets.ClientWebSocket
                $cancel = New-Object System.Threading.CancellationTokenSource
                try {
                    $url = 'http://{0}:{1}/cert/root-ca' -f $serverIP, $httpPorts[$i]
                    $response = Invoke-WebRequest -Uri $url -UseBasicParsing -TimeoutSec 3
                    $pem = if ($response.Content -is [byte[]]) { [Text.Encoding]::UTF8.GetString($response.Content) } else { [string]$response.Content }
                    if ($pem -notmatch '-----BEGIN CERTIFICATE-----') { throw 'Root certificate download failed.' }
                    $cancel.CancelAfter(3000)
                    $task = $socket.ConnectAsync([Uri]('wss://{0}:{1}/' -f $serverIP, $securePorts[$i]), $cancel.Token)
                    $task.GetAwaiter().GetResult()
                    if ($socket.State -ne [Net.WebSockets.WebSocketState]::Open) { throw 'Secure WebSocket did not open.' }
                    $printPort = $securePorts[$i]
                    $certificateUrl = $url
                    break
                } catch { $lastFailure = $_.Exception.Message }
                finally { $socket.Dispose(); $cancel.Dispose() }
            }
            if (-not $printPort) { Start-Sleep -Seconds 1 }
        }
        if (-not $printPort) { throw "QZ was configured but verification failed: $lastFailure Check QZ's tray/log and run Test connection in the app." }
        $address = '{0}:{1}' -f $serverIP, $printPort
        Write-Host ''
        Write-Host "QZ Server activated successfully at IP: $address" -ForegroundColor Green
        Write-Host "Paste into step 2: $address"
        Write-Host "Certificate: $certificateUrl"
        Write-Host 'Verified on this PC. Test from the phone/tablet in step 5.'
        try { Set-Clipboard -Value $address; Write-Host 'Server address copied.' } catch { Write-Host 'Copy the server address above.' }
    } catch {
        Write-Host ''
        Write-Host "QZ setup failed: $($_.Exception.Message)" -ForegroundColor Red
    } finally {
        if ($taskName) { Unregister-ScheduledTask -TaskName $taskName -Confirm:$false -ErrorAction SilentlyContinue }
    }
}
`;

const QZServerGuide = ({ formik, testConnection, loadPrinters, printTestPdf, titleId }: Omit<QZServerDialogProps, 'open' | 'onClose'> & { titleId: string }) => {
  const { settings: savedSettings } = useSettingsContext();
  const { updateMessageStatus } = useMessageContext();
  const [addressResetKey, setAddressResetKey] = useState(0);
  const settings = readSettings(formik);
  const host = settings.qzHost.trim(), port = settings.qzPort.trim();
  const invalid = Boolean(Object.keys(validateQZServer(settings)).length);
  const ready = Boolean(host) && !invalid;
  const certificatePort = ({ '8181': 8182, '8282': 8283, '8383': 8384, '8484': 8485 } as Record<string, number>)[port || '8181'] ?? 8182;
  const certificateUrl = ready ? `http://${host}:${certificatePort}/cert/root-ca` : '';
  const [platform, setPlatform] = useState('ios');
  const [mode, setMode] = useState<QZConnectionMode>('remote');
  const [showScript, setShowScript] = useState(false);
  const [connection, setConnection] = useState<QZConnection | null>(null);
  const [printers, setPrinters] = useState<string[]>([]);
  const [printer, setPrinter] = useState('');
  const [busy, setBusy] = useState('');
  const pending = useRef(false), mounted = useRef(false);
  const key = JSON.stringify([host, port, getIn(formik.values, 'DispatchPrinting.useCertificates')]);
  const currentKey = useRef(key);
  currentKey.current = key;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setConnection(null); setPrinters([]); setPrinter(''); }, [key]);
  const report = (status: 'success' | 'error', message: string) => updateMessageStatus({ requestUrl: 'printing/qz-setup', status, message });
  const run = async (label: string, action: (current: () => boolean) => Promise<void>) => {
    if (pending.current) return;
    pending.current = true;
    const startedKey = currentKey.current;
    const current = () => mounted.current && startedKey === currentKey.current;
    setBusy(label);
    try { await action(current); }
    catch (error) { if (current()) report('error', error instanceof Error ? error.message : String(error)); }
    finally { pending.current = false; if (mounted.current) setBusy(''); }
  };
  const save = () => {
    void run('Saving', async (current) => {
      if (formik.isSubmitting || !ready) return;
      const errors = await formik.validateForm();
      if (!current()) return;
      if (Object.keys(errors).length) { report('error', 'Settings could not be saved. Check the settings page for validation errors.'); return; }
      await formik.submitForm();
    });
  };
  const copy = (text: string) => {
    void run('Copying', async (current) => {
      if (!navigator.clipboard?.writeText) throw new Error('Select and copy the text manually.');
      await navigator.clipboard.writeText(text);
      if (current()) report('success', 'Copied.');
    });
  };
  const reload = () => {
    void run('Reloading', async () => {
      const saved = savedSettings?.DispatchPrinting;
      const qzHost = String(saved?.qzHost ?? ''), qzPort = String(saved?.qzPort ?? '');
      await formik.setValues((previous: any) => ({ ...previous, DispatchPrinting: { ...previous.DispatchPrinting, qzHost, qzPort } }));
      if (mounted.current) setAddressResetKey((previous) => previous + 1);
    });
  };
  const connect = () => {
    void run('Connecting', async (current) => {
      setConnection(null); setPrinters([]); setPrinter('');
      const result = await testConnection(mode);
      if (current()) { setConnection(result); report('success', `Connected: ${result.host}:${result.port} (${result.secure ? 'WSS' : 'WS'}).`); }
    });
  };
  const refresh = () => {
    if (connection) void run('Loading printers', async (current) => {
      setPrinters([]); setPrinter('');
      const names = await loadPrinters(connection);
      if (current()) { setPrinters(names); report('success', names.length ? `${names.length} printers found.` : 'No printers found on this server.'); }
    });
  };
  const print = () => {
    if (connection && printer) void run('Printing', async (current) => {
      await printTestPdf(connection, printer);
      if (current()) report('success', `PDF sent to ${printer}. Check the printed page.`);
    });
  };
  return <>
    <DialogTitle id={titleId}>QZ Print Server</DialogTitle>
    <DialogContent dividers>
      <Typography variant="overline" color="text.secondary">Computer</Typography>
      <Step title="1 A. Activate QZ server">
        <Typography variant="body2">PowerShell → Run as administrator → paste script.</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Copy setup script" disabled={Boolean(busy)} onClick={() => copy(QZ_SERVER_SETUP_SCRIPT)} /></Box>
          <Box sx={{ width: 'fit-content' }}><CustomButton title={showScript ? 'Hide script' : 'View script'} variant="text" onClick={() => setShowScript(!showScript)} /></Box>
        </Box>
        {showScript && <Box component="pre" sx={{ m: 0, p: 1.5, maxHeight: 280, overflow: 'auto', bgcolor: 'action.hover', fontSize: 12 }}>{QZ_SERVER_SETUP_SCRIPT}</Box>}
        <Typography variant="caption" color="text.secondary">Requires QZ Tray 2.2+. Run once; rerunning replaces certificate trust.</Typography>
      </Step>
      <Box sx={{ px: 2, my: 1, borderLeft: 4, borderColor: 'warning.main', bgcolor: 'action.hover', borderRadius: 1 }}>
        <Step title="1 B. Restart QZ Tray manually">
          <Typography variant="body2" color="text.secondary">Usually needed. Quit QZ Tray → reopen.</Typography>
        </Step>
      </Box>
      <Step title="2. Server IP">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Save" disabled={Boolean(busy) || formik.isSubmitting || !ready} isSubmitting={busy === 'Saving' || formik.isSubmitting} onClick={save} /></Box>
          <Box sx={{ flex: 1, minWidth: 180 }}><QZServerFields formik={formik} disabled={Boolean(busy) || formik.isSubmitting} resetKey={addressResetKey} /></Box>
          <Box sx={{ width: 'fit-content' }}><CustomButton title={busy === 'Reloading' ? 'Loading…' : 'Get saved IP'} variant="outlined" disabled={Boolean(busy) || formik.isSubmitting} onClick={reload} /></Box>
        </Box>
      </Step>
      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}><Typography variant="overline" color="text.secondary">Phone / tablet</Typography></Box>
      <Step title="3. Get certificate">
        {certificateUrl && <Box sx={{ bgcolor: 'white', p: 1, alignSelf: 'center' }}><QRCodeSVG value={certificateUrl} size={200} marginSize={4} title="Download QZ root certificate" /></Box>}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Download certificate" variant="outlined" disabled={!certificateUrl} onClick={() => window.open(certificateUrl, '_blank', 'noopener,noreferrer')} /></Box>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Copy link" variant="text" disabled={!certificateUrl || Boolean(busy)} onClick={() => copy(certificateUrl)} /></Box>
        </Box>
        {certificateUrl && <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{certificateUrl}</Typography>}
      </Step>
      <Step title="4. Install certificate">
        <CustomField options={{ name: 'qzPlatform', label: 'Device', select: true, topMargin: '0' }} stateProps={{ state: platform, setState: setPlatform }} sx={{ my: 0 }}><MenuItem value="ios">iPad / iPhone</MenuItem><MenuItem value="android">Android</MenuItem></CustomField>
        {platform === 'ios' ? <>
          <Typography variant="body2">Open the link in Safari → Allow.</Typography>
          <Typography variant="body2">Settings → General → VPN &amp; Device Management → downloaded profile → Install.</Typography>
          <Typography variant="body2">Settings → General → About → Certificate Trust Settings → enable full trust.</Typography>
        </> : <>
          <Typography variant="body2">Settings → Security &amp; privacy → More security settings → Encryption &amp; credentials → Install a certificate → CA certificate.</Typography>
          {/* <Typography variant="caption" color="text.secondary">Menus vary; search “Install a certificate”. Chrome trust varies too—verify with the connection test.</Typography> */}
        </>}
        {/* <Typography variant="caption" color="text.secondary">Open this app on the device → Reload settings → test below. Reload replaces the address only.</Typography> */}
      </Step>
      <Step title="5. Test printing">
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <CustomField options={{ name: 'qzMode', label: 'Connection', select: true, disabled: Boolean(busy), topMargin: '0' }} stateProps={{ state: mode, setState: setMode }} sx={{ flex: 1, minWidth: 190, my: 0 }}><MenuItem value="remote">Configured server</MenuItem><MenuItem value="local">Local QZ</MenuItem><MenuItem value="automatic">Automatic (local first)</MenuItem></CustomField>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Test connection" variant="outlined" disabled={Boolean(busy) || (mode === 'remote' ? !ready : mode === 'automatic' && invalid)} onClick={connect} /></Box>
        </Box>
        {connection && <Typography variant="caption">Connected: {connection.host}:{connection.port}</Typography>}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <CustomField options={{ name: 'qzPrinter', label: 'Test printer', select: true, disabled: Boolean(busy) || !printers.length, topMargin: '0' }} stateProps={{ state: printer, setState: setPrinter }} sx={{ flex: 1, minWidth: 190, my: 0 }}><MenuItem value="">Select printer</MenuItem>{printers.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}</CustomField>
          <Box sx={{ width: 'fit-content' }}><CustomButton title="Refresh printers" variant="text" disabled={Boolean(busy) || !connection} onClick={refresh} /></Box>
        </Box>
        <Box sx={{ width: 'fit-content' }}><CustomButton title="Test print PDF" disabled={Boolean(busy) || !connection || !printer} onClick={print} /></Box>
        <Typography variant="caption" color="text.secondary">Then choose your printers in settings.</Typography>
      </Step>
      {busy && <Typography role="status" aria-live="polite">{busy}…</Typography>}
    </DialogContent>
  </>;
};

export type QZServerSettings = { qzHost: string; qzPort: string };

export type QZConnectionMode = 'automatic' | 'local' | 'remote';

export type QZConnection = { id: number; host: string; port: number; secure: boolean; mode: 'local' | 'remote' };

// Return freshly fetched settings, not an HTTP response wrapper. Reload preserves other form values.
export type ReloadQZSettings = () => Promise<{ DispatchPrinting?: Partial<QZServerSettings> }>;

export type QZServerDialogProps = {
  open: boolean; onClose: () => void; formik: FormikProps<any>; reloadSettings?: ReloadQZSettings;
  // These callbacks belong to Printer Manager and must use its existing QZ operation queue.
  testConnection: (mode: QZConnectionMode) => Promise<QZConnection>;
  loadPrinters: (connection: QZConnection) => Promise<string[]>;
  // Submit one generated pixel PDF to the tested connection; never retry on another server.
  printTestPdf: (connection: QZConnection, printer: string) => Promise<void>;
};
